import type { ReaderAiBlock } from "@/types/agentic-chat";
import type { StudentToofiSurface } from "@/types/student-toofi";

export const TOOFI_ARTIFACT_STORAGE_EVENT = "athr:toofi-artifacts";
export const TOOFI_ARTIFACT_INDEX_KEY = "athr.toofi.artifacts.index";
export const TOOFI_ARTIFACT_MIGRATION_KEY = "athr.toofi.artifacts.migrated.v1";

export type ToofiArtifactScope = "workspace" | "reader";
export type ToofiArtifactSurfaceOrigin = StudentToofiSurface | "reader" | "unknown";
export type ToofiArtifactKind =
  | "plan_overview"
  | "plan_milestones"
  | "daily_plan"
  | "plan_checkin"
  | "note"
  | "mcq"
  | "essay_prompt"
  | "comparison"
  | "worked_example"
  | "summary"
  | "table"
  | "formula_sheet";
export type ToofiArtifactPayload = Extract<ReaderAiBlock, { type: ToofiArtifactKind }>;

export type ToofiArtifactRecord = {
  id: string;
  kind: ToofiArtifactKind;
  scope: ToofiArtifactScope;
  threadId: string;
  messageId: string;
  blockId: string;
  resourceId: string | null;
  linkedPlanId: string | null;
  surfaceOrigin: ToofiArtifactSurfaceOrigin;
  title: string;
  preview: string;
  createdAt: number;
  updatedAt: number;
  saved: boolean;
  pinned: boolean;
  sourcePage: number | null;
  sourcePageLabel: string | null;
  sourcePageRoute: string | null;
  sourcePageKind: "reader" | "workspace" | null;
  sourcePagePath: string | null;
  payload: ToofiArtifactPayload;
};

export type ToofiArtifactRecordIndexItem = Omit<ToofiArtifactRecord, "payload">;

export const TOOFI_ARTIFACT_BLOCK_TYPES: ToofiArtifactKind[] = [
  "plan_overview",
  "plan_milestones",
  "daily_plan",
  "plan_checkin",
  "note",
  "mcq",
  "essay_prompt",
  "comparison",
  "worked_example",
  "summary",
  "table",
  "formula_sheet",
];

export const isToofiArtifactBlock = (block: ReaderAiBlock): block is ToofiArtifactPayload =>
  TOOFI_ARTIFACT_BLOCK_TYPES.includes(block.type as ToofiArtifactKind);

const trimPreview = (value: string, maxLength = 160) => {
  const normalized = value.replace(/\s+/g, " ").trim();
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 1).trim()}...`;
};

const getMcqPrompt = (block: Extract<ReaderAiBlock, { type: "mcq" }>) =>
  block.prompt ?? block.questions?.[0]?.prompt ?? "Quick quiz";

const getEssayPrompt = (block: Extract<ReaderAiBlock, { type: "essay_prompt" }>) =>
  block.prompt ?? block.prompts?.[0]?.prompt ?? "Essay prompt";

export const deriveToofiArtifactMetadata = (
  block: ToofiArtifactPayload,
): { kind: ToofiArtifactKind; title: string; preview: string } => {
  switch (block.type) {
    case "plan_overview":
      return {
        kind: block.type,
        title: block.title || "Study plan",
        preview: trimPreview(block.goalSummary || block.horizonLabel || "Study plan"),
      };
    case "plan_milestones":
      return {
        kind: block.type,
        title: "Plan milestones",
        preview: trimPreview(block.items.map((item) => item.title).join(" • ") || "Milestones"),
      };
    case "daily_plan":
      return {
        kind: block.type,
        title: "Daily plan",
        preview: trimPreview(block.items.map((item) => item.text).join(" • ") || "Daily plan"),
      };
    case "plan_checkin":
      return {
        kind: block.type,
        title: block.title || "Plan check-in",
        preview: trimPreview(block.summary || block.nextAction || "Plan check-in"),
      };
    case "note":
      return {
        kind: block.type,
        title: block.title || "Study note",
        preview: trimPreview(block.content || "Study note"),
      };
    case "mcq":
      return {
        kind: block.type,
        title: "Quick quiz",
        preview: trimPreview(getMcqPrompt(block)),
      };
    case "essay_prompt":
      return {
        kind: block.type,
        title: "Essay prompt",
        preview: trimPreview(getEssayPrompt(block)),
      };
    case "comparison":
      return {
        kind: block.type,
        title: block.title || `${block.leftLabel} vs ${block.rightLabel}`,
        preview: trimPreview(block.rows.map((row) => row.topic).join(" • ") || "Comparison"),
      };
    case "worked_example":
      return {
        kind: block.type,
        title: block.title || "Worked example",
        preview: trimPreview(block.takeaway || block.steps[0] || "Worked example"),
      };
    case "summary":
      return {
        kind: block.type,
        title: "Summary",
        preview: trimPreview(block.items.join(" • ") || "Summary"),
      };
    case "table":
      return {
        kind: block.type,
        title: "Study table",
        preview: trimPreview(block.columns.join(" • ") || "Study table"),
      };
    case "formula_sheet":
      return {
        kind: block.type,
        title: block.title || "Formula sheet",
        preview: trimPreview(block.items.map((item) => item.label).join(" • ") || "Formula sheet"),
      };
    default:
      return {
        kind: block.type,
        title: "Artifact",
        preview: "Saved from Toofi",
      };
  }
};

